import { Injectable } from "@angular/core";
import {
  ActivatedRouteSnapshot,
  BaseRouteReuseStrategy
} from "@angular/router";

/**
 * Keeps a single component instance alive when toggling between a feature's base
 * route and any of its record siblings — `/map` ↔ `/map/tracker/:id` ↔
 * `/map/site/:code`, and the same three on the list.
 *
 * Those are declared as siblings pointing at the same component (see
 * app.routes.ts). With Angular's default strategy, navigating between them
 * destroys and recreates the component — tearing down the Leaflet map and the
 * websocket (map) or the loaded rows (list), and discarding the in-hand
 * selection. That made on-page tracker selection (which navigates to the
 * tracker URL to reflect it) flicker and fall back to the base route instead of
 * opening the detail.
 *
 * Reusing the component keeps it mounted; the router still updates the route's
 * `paramMap`, so each page's `route.paramMap` subscription drives the detail
 * open/close. Every other transition keeps the default behavior, so deep-links
 * and cross-tab jumps (different components) still recreate and fetch as before.
 *
 * Which segments those are is not decided here: the tab declares
 * `reuseAcrossDetail` (src/app/tabs.ts), app.routes.ts publishes it as route
 * `data`, and this strategy reads it off the snapshot. Naming the two components
 * instead is what made this file import two pages to answer a routing question,
 * and left a third page with a record sibling one edit away from flickering.
 */
@Injectable()
export class TabDetailRouteReuseStrategy extends BaseRouteReuseStrategy {
  override shouldReuseRoute(
    future: ActivatedRouteSnapshot,
    curr: ActivatedRouteSnapshot
  ): boolean {
    const isDetailHost = future.data?.["reuseAcrossDetail"] === true;
    if (future.component === curr.component && isDetailHost) {
      return true;
    }
    return super.shouldReuseRoute(future, curr);
  }
}
